import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_THEME, THEMES, applyTheme, type ThemeChoice } from '@forecastify/ui';
import { ConsoleShell, type ShellItem } from './chrome/console-shell';
import { PageChromeProvider } from './chrome/page-chrome';
import { ToastProvider } from './chrome/toasts';
import {
  I18nProvider,
  LOCALES,
  preferredLocale,
  useT,
  type Locale,
  useI18n,
  type MessageKey,
} from './i18n';
import { ENROL_PATH, HOME_PATH, ROUTES, SIGNED_OUT_PATH, SIGN_IN_PATH } from './routes';
import { OperatorSessionProvider, useOperatorSession } from './session';
import { failedJobCount, useHealth } from './use-health';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The console's router and its one gate.                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **BOTH HALVES ARE DRIVEN BY `routes.tsx`**, exactly as the product's are: the sidebar is
 * `ROUTES.filter(entrance === 'sidebar')`, so a screen added with an entrance gets a link with no
 * further work, and one added without has to write down why. A navigation written by hand beside a
 * route table written by hand drifts on the first screen somebody adds in a hurry, and the drift is
 * invisible because both halves are individually correct.
 */
/* ⚠️ Prefixed, because the console and the product can share an origin behind one proxy and
   a bare `theme` would then be one preference for two applications. */
const SIDEBAR_KEY = 'cp.sidebar.collapsed';
const THEME_KEY = 'cp.theme';
const LOCALE_KEY = 'cp.locale';

/** ⚠️ Guarded for `localStorage`'s reason below: the accessor itself can throw. */
function storedLocale(): Locale | undefined {
  try {
    const value = globalThis.localStorage.getItem(LOCALE_KEY);
    return LOCALES.find((locale) => locale === value);
  } catch {
    return undefined;
  }
}

export function App({ locale }: { readonly locale?: Locale } = {}): ReactNode {
  /**
   * ⚠️⚠️ **STATE, NOT A PROP READ ONCE.** The language switch used to write a key and reload, and
   * nothing read the key back — the page returned in the language it left in. An explicit `locale`
   * (a test pinning one) still wins over the stored choice, which still wins over the browser's.
   */
  const [current, setCurrent] = useState<Locale>(
    () => locale ?? storedLocale() ?? preferredLocale(),
  );
  const switchLocale = useCallback((next: Locale): void => {
    setCurrent(next);
    try {
      globalThis.localStorage.setItem(LOCALE_KEY, next);
    } catch {
      /* A choice that cannot be remembered still applies for this session. */
    }
  }, []);

  return (
    <I18nProvider locale={current} onLocaleChange={switchLocale}>
      {/* ⚠️ Inside i18n, because a sign-in refusal has to be rendered in words; outside the router,
          because restoring a session is not a route. */}
      <OperatorSessionProvider>
        {/* Inside i18n, because a toast is words; around the gate, so a sign-in can toast too. */}
        <ToastProvider>
          <Gate />
        </ToastProvider>
      </OperatorSessionProvider>
    </I18nProvider>
  );
}

/**
 * ⚠️ **Three states, and conflating any two of them is a bug somebody reports as "it hangs".**
 * `restoring` is the one that gets skipped — rendering the sign-in card while a stored token is
 * still being checked flashes a login form at somebody who is signed in, on every reload.
 */
function Gate(): ReactNode {
  const t = useT();
  const session = useOperatorSession();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * ⚠️⚠️ **THE OPEN ROUTES ARE CHECKED FIRST, AND `/enrol` IS WHY THIS MATTERS RATHER THAN BEING
   * TIDY.** Below this point the signed-out branch renders the sign-in card for every address —
   * which would swallow enrolment, the one screen an operator without a factor needs and the one
   * they cannot reach with a session. The product's `Gate` has the same shape for its invitation
   * page and records the same reason.
   *
   * ⚠️ **Through a `<Route>`, not as a bare element**, so `useParams()` works. An element rendered
   * outside any route gets an empty params object; `apps/web` shipped that bug on its invitation
   * screen and every link opened onto *"that link is not valid"*.
   */
  const open = ROUTES.find(
    (route) => route.signedOutOk === true && matchPath(route.path, location.pathname) !== null,
  );

  /* ⚠️ Signing in lands on the list rather than leaving somebody on the card they just used — the
   * product reported that as *"it just put me back onto the sign in screen"*. An effect, not a
   * render-time redirect, because a ref mutated during render does not survive StrictMode's double
   * invocation. */
  useEffect(() => {
    if (session.status !== 'signed-in') return;
    if (location.pathname === SIGN_IN_PATH || location.pathname === ENROL_PATH) {
      void navigate(HOME_PATH, { replace: true });
    }
  }, [session.status, location.pathname, navigate]);

  /**
   * ⚠️ The document title, because rule 7 names page titles explicitly — and an operator with both
   * applications open needs the tab to say which one this is. The TAB, not the breadcrumb: the
   * first version printed this string in the header, suffix and all.
   *
   * ⚠️ 3 Nov 2026 · **HERE, IN THE GATE, AND NOT IN THE CHROME.** It lived in `ChromeInside`, which
   * only renders for a session — so `/sign-in`, `/enrol`, `/join` and `/signed-out` kept
   * `index.html`'s static title, and the sign-in card shown after a session ended kept the last
   * screen's (*Szervezetek*). Signed out, an address that is not an open route shows the sign-in
   * card, so it takes the sign-in card's title.
   */
  const titled =
    session.status === 'restoring'
      ? undefined
      : session.status === 'signed-out'
        ? (open ?? ROUTES.find((route) => route.path === SIGN_IN_PATH))
        : ROUTES.find((route) => matchPath(route.path, location.pathname) !== null);
  useEffect(() => {
    if (titled === undefined) return;
    document.title = t(titled.title);
  }, [titled, t]);

  if (session.status === 'signed-out' && open !== undefined) {
    return (
      <Routes>
        <Route path={open.path} element={open.element} />
      </Routes>
    );
  }

  if (session.status === 'restoring') {
    return (
      <div className="grid min-h-screen place-items-center">
        <p role="status" className="text-muted-foreground text-[13px]">
          {t('tenants.loading')}
        </p>
      </div>
    );
  }

  if (session.status === 'signed-out') {
    /* ⚠️ Rendered rather than redirected, so the address bar is not rewritten while somebody is
     * still typing their password — and so the address they came for is still there afterwards. */
    const signIn = ROUTES.find((route) => route.path === SIGN_IN_PATH);
    return <>{signIn?.element}</>;
  }

  return <Chrome />;
}

function Chrome(): ReactNode {
  return (
    <PageChromeProvider>
      <ChromeInside />
    </PageChromeProvider>
  );
}

function ChromeInside(): ReactNode {
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useOperatorSession();
  /* ⚠️⚠️ **BEFORE THE EARLY RETURNS BELOW, AND IT WAS NOT.** A hook called after an early return runs
     on some renders and not others — *Rendered more hooks than during the previous render*, which
     React throws rather than tolerating. The route suite caught it once already. */
  const { locale, setLocale } = useI18n();
  const health = useHealth();

  /**
   * ⚠️⚠️ **BOTH READS ARE WRAPPED, BECAUSE `localStorage` THROWS RATHER THAN RETURNING NULL.**
   * In a private window, with site data blocked, or under a jsdom that never defined it, the
   * ACCESSOR itself raises — so an unguarded read takes the whole console down before it draws a
   * pixel. The fallback in each case is the shipped default.
   */
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return globalThis.localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [theme, setTheme] = useState<ThemeChoice>(() => {
    try {
      return globalThis.localStorage.getItem(THEME_KEY) ?? DEFAULT_THEME;
    } catch {
      return DEFAULT_THEME;
    }
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const remember = useCallback((key: string, value: string): void => {
    try {
      globalThis.localStorage.setItem(key, value);
    } catch {
      /* ⚠️ A preference that cannot be stored is still a preference for this session. */
    }
  }, []);

  const current = ROUTES.find((route) => matchPath(route.path, location.pathname) !== null);

  /* ⚠️ A signed-in operator is not left on a signed-out screen. */
  if (current?.signedOutOk === true) return <Navigate to={HOME_PATH} replace />;

  if (current?.chrome === 'bare') return <>{current.element}</>;

  /**
   * ⚠️⚠️ **THE BADGE IS A COUNT OF THINGS TO GO AND DO, NOT A COUNT OF ROWS** — and its colour
   * says how urgent: red for broken work, amber for the agent ceiling, muted for a suspension or
   * somebody who has not enrolled yet. The mockup's four, exactly.
   *
   * ⚠️ **Silent until the read lands, and silent if it fails.** A badge asserts that something IS
   * wrong; absent is the only honest rendering of *we do not know yet*.
   */
  const badgeFor = (path: string): Pick<ShellItem, 'badge' | 'badgeTone'> => {
    if (health.kind !== 'ready') return {};
    const h = health.health;
    if (path === '/tenants') return { badge: h.suspended.length, badgeTone: 'muted' };
    if (path === '/operations') {
      return { badge: h.overdue.length + failedJobCount(h), badgeTone: 'bad' };
    }
    if (path === '/agent-spend') return { badge: h.atCeiling.length, badgeTone: 'warn' };
    if (path === '/operators') return { badge: h.unenrolledOperators, badgeTone: 'muted' };
    return {};
  };

  /**
   * ⚠️⚠️ **BUILT FROM `ROUTES`, NOT LISTED HERE.** A row appears because a screen declared an
   * entrance and a group; it cannot appear because somebody remembered to add it in two places.
   *
   * ⚠️ **Active on the organisation's own sub-pages too** — the detail and the deletion screen
   * sit under *Szervezetek* in the mockup, so the row stays lit while you are inside one.
   */
  const GROUPS = [
    { id: 'tenants', label: 'nav.group.tenants' },
    { id: 'operations', label: 'nav.group.operations' },
    { id: 'oversight', label: 'nav.group.oversight' },
  ] as const;

  const groups = GROUPS.map((group) => ({
    label: t(group.label),
    items: ROUTES.filter(
      (route) => route.entrance === 'sidebar' && route.navGroup === group.id,
    ).map((route): ShellItem => ({
      ...badgeFor(route.path),
      label: t(route.navLabel ?? route.title),
      icon: route.navIcon ?? 'square-dashed',
      href: route.path,
      active:
        matchPath(route.path, location.pathname) !== null ||
        (route.path === '/tenants' && location.pathname.startsWith('/tenants/')),
    })),
  })).filter((group) => group.items.length > 0);

  const name = session.operator?.name ?? session.operator?.email ?? '';

  return (
    <ConsoleShell
      appName={t('app.console')}
      /* ⚠️ **The trail a screen gets when it declares none** — its own sidebar label, which is
         what the mockup shows on every list. A prop rather than a `useCrumbs` call here, because a
         parent's effect runs AFTER its children's and would overwrite the trail the organisation
         detail just declared. */
      fallbackCrumb={current === undefined ? undefined : t(current.navLabel ?? 'nav.tenants')}
      env={{
        /* ⚠️ Read at call time, never captured at module scope — a value captured at import is a
           value a test cannot stub. A deployment names itself; a dev server says it is one rather
           than claiming to be production, which is what the mockup's sample *Éles* would do. */
        label:
          typeof import.meta.env.VITE_ENV_LABEL === 'string'
            ? import.meta.env.VITE_ENV_LABEL
            : import.meta.env.DEV
              ? t('env.development')
              : t('env.production'),
        live: !import.meta.env.DEV,
      }}
      groups={groups}
      collapsed={collapsed}
      onCollapsedChange={(next) => {
        setCollapsed(next);
        remember(SIDEBAR_KEY, next ? '1' : '0');
      }}
      theme={{
        value: theme,
        label: t('nav.theme'),
        /* ⚠️ The design system's eight, named in this catalogue — `ThemeDefinition` carries no label
           because `packages/ui` is string-agnostic under rule 7. The mockup draws its own list
           (Rosé Pine, Solarized, Gruvbox); themes are tokens, and tokens are the design system's. */
        options: THEMES.map((option) => ({
          value: option.id,
          label: t(`theme.${option.id}` as MessageKey),
        })),
      }}
      onThemeChange={(value) => {
        setTheme(value);
        remember(THEME_KEY, value);
      }}
      locale={locale}
      locales={setLocale === undefined ? [] : LOCALES}
      onLocaleChange={(value) => {
        const next = LOCALES.find((option) => option === value);
        if (next !== undefined) setLocale?.(next);
      }}
      me={{ name, email: session.operator?.email ?? '', initials: initialsOf(name) }}
      onSignOut={() => {
        /* ⚠️ 3 Nov 2026 · an ended session lands on `/signed-out`, as `routes.tsx` promises. Only
           after the session is gone: navigating first would meet the redirect above, which sends a
           signed-in operator off a signed-out screen. A 401 still shows the card in place. */
        void session.signOut().then(() => navigate(SIGNED_OUT_PATH, { replace: true }));
      }}
      labels={{
        collapse: t('nav.collapse'),
        expand: t('nav.expand'),
        signOut: t('nav.signOut'),
        navigation: t('nav.main'),
        breadcrumbs: t('nav.breadcrumbs'),
        skipToContent: t('nav.skipToContent'),
        language: t('nav.language'),
      }}
    >
      <Routes>
        <Route path="/" element={<Navigate to={HOME_PATH} replace />} />
        {ROUTES.filter((route) => route.chrome === 'shell').map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        {/* ⚠️ The catch-all is LAST and explicit. A wildcard that renders a plausible screen makes a
            typo'd path look like a working one — one of the four failures §4 names. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ConsoleShell>
  );
}

/** Two letters from a name — *Petáki Adrián* is *PA*, the mockup's *KD* for *Kovács Dóra*. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0] ?? '');
  return letters.join('').toUpperCase() || '?';
}

function NotFound(): ReactNode {
  const location = useLocation();
  return (
    <div data-screen="not-found" className="p-8">
      <p className="text-[13px] font-medium">404</p>
      {/* The path is not translated: it is the address the browser has, verbatim, in mono. */}
      <p className="text-muted-foreground mt-1 text-[12px]">{location.pathname}</p>
    </div>
  );
}
